import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { 
  ArrowRight, Newspaper, MapPin, Users, Zap, Shield,
  CheckCircle, TrendingUp, Eye, BookOpen, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://businessmintpr.com';

// Schema.org structured data for Print Media Service
const printMediaSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Print Media Coverage Services",
  "description": "Get your brand featured in India's leading newspapers and magazines with verified circulation and pan-India coverage.",
  "provider": {
    "@type": "Organization",
    "name": "BusinessMint PR",
    "url": SITE_URL
  },
  "serviceType": "Print Media Advertising",
  "areaServed": {
    "@type": "Country",
    "name": "India"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Print Media Packages",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Newspaper Coverage",
          "description": "National and regional daily newspapers with massive readership"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Magazine Features",
          "description": "Industry and lifestyle magazines for targeted audiences"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Regional Publications",
          "description": "Local newspapers and magazines for geo-targeted campaigns"
        }
      }
    ]
  }
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": SITE_URL
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Services",
      "item": `${SITE_URL}/marketplace`
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": "Print Media",
      "item": `${SITE_URL}/services/print-media`
    }
  ]
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What types of print media do you offer?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We offer placements in national newspapers (Times of India, Hindustan Times, etc.), regional papers, business magazines, lifestyle publications, and industry-specific journals. Coverage options range from news articles to advertorials and press releases."
      }
    },
    {
      "@type": "Question",
      "name": "How long does print media placement take?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Turnaround depends on the publication's editorial calendar. Daily newspapers can publish within 3-7 days, while magazines may take 2-4 weeks depending on their print cycle. We provide estimated timelines for each publication."
      }
    },
    {
      "@type": "Question",
      "name": "Do I receive physical copies?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes! For newspaper features, we provide high-resolution scans. For magazines, we can arrange physical copies to be shipped to your address (additional shipping charges may apply)."
      }
    },
    {
      "@type": "Question",
      "name": "Can I target specific regions?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Absolutely. We offer regional newspaper options across major cities and states in India. This is perfect for businesses targeting specific geographic markets."
      }
    }
  ]
};

const PrintMedia = () => {
  const [printMedia, setPrintMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    fetchPrintMedia();
  }, []);

  const fetchPrintMedia = async () => {
    try {
      const res = await fetch(`${API_URL}/api/print-media`);
      const data = await res.json();
      setPrintMedia(data.slice(0, 6));
    } catch (err) {
      console.error('Error fetching print media:', err);
    } finally {
      setLoading(false);
    }
  };

  const mediaTypes = [
    {
      icon: Newspaper,
      name: 'Newspapers',
      description: 'National and regional daily newspapers with massive readership',
      reach: '50M+ Daily Readers'
    },
    {
      icon: BookOpen,
      name: 'Magazines',
      description: 'Industry and lifestyle magazines for targeted audiences',
      reach: '10M+ Monthly Readers'
    },
    {
      icon: MapPin,
      name: 'Regional Publications',
      description: 'Local newspapers and magazines for geo-targeted campaigns',
      reach: 'City-wise Coverage'
    }
  ];

  const features = [
    {
      icon: Users,
      title: 'Massive Reach',
      description: 'Connect with millions of readers across India through established publications.'
    },
    {
      icon: Eye,
      title: 'High Credibility',
      description: 'Print media carries unmatched trust and authority with readers.'
    },
    {
      icon: Zap,
      title: 'Fast Publishing',
      description: 'Get featured in upcoming editions with our priority placement service.'
    },
    {
      icon: Shield,
      title: 'Proof of Publication',
      description: 'Receive physical copies and digital scans as proof of your feature.'
    }
  ];

  const benefits = [
    'Reach offline audiences who trust print media',
    'Physical copies for your press kit and office',
    'High recall value compared to digital ads',
    'Regional targeting for local businesses',
    'Premium positioning options available',
    'Long shelf life - magazines kept for months'
  ];

  const faqs = [
    {
      question: 'What types of print media do you offer?',
      answer: 'We offer placements in national newspapers (Times of India, Hindustan Times, etc.), regional papers, business magazines, lifestyle publications, and industry-specific journals. Coverage options range from news articles to advertorials and press releases.'
    },
    {
      question: 'How long does print media placement take?',
      answer: 'Turnaround depends on the publication\'s editorial calendar. Daily newspapers can publish within 3-7 days, while magazines may take 2-4 weeks depending on their print cycle. We provide estimated timelines for each publication.'
    },
    {
      question: 'Do I receive physical copies?',
      answer: 'Yes! For newspaper features, we provide high-resolution scans. For magazines, we can arrange physical copies to be shipped to your address (additional shipping charges may apply).'
    },
    {
      question: 'Can I target specific regions?',
      answer: 'Absolutely. We offer regional newspaper options across major cities and states in India. This is perfect for businesses targeting specific geographic markets.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Print Media Coverage India | Newspapers & Magazines | BusinessMint PR</title>
        <meta name="title" content="Print Media Coverage India | Newspapers & Magazines | BusinessMint PR" />
        <meta name="description" content="Get your brand featured in India's leading newspapers and magazines. 50M+ daily readers, 30+ publications, pan-India coverage. Physical copies and publication proof included." />
        <meta name="keywords" content="print media India, newspaper advertising, magazine coverage, press coverage India, newspaper feature, magazine advertisement, regional newspaper, national newspaper advertising" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="author" content="BusinessMint PR" />
        <link rel="canonical" href={`${SITE_URL}/services/print-media`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/services/print-media`} />
        <meta property="og:title" content="Print Media Coverage | Newspapers & Magazines" />
        <meta property="og:description" content="Get featured in India's leading newspapers and magazines. 50M+ daily readers, national and regional coverage." />
        <meta property="og:site_name" content="BusinessMint PR" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`${SITE_URL}/services/print-media`} />
        <meta name="twitter:title" content="Print Media Coverage | BusinessMint PR" />
        <meta name="twitter:description" content="30+ publications, 50M+ readers. Newspapers and magazines with verified circulation and proof of publication." />
        
        {/* Schema.org Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(printMediaSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0d0d0d] py-20 md:py-32">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(90deg, #D4AF37 1px, transparent 1px),
                             linear-gradient(#D4AF37 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-manrope uppercase tracking-widest mb-6">
              <Newspaper className="w-4 h-4" />
              Print Media Services
            </div>
            
            <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white">
              Get Featured in{' '}
              <span className="text-[#D4AF37]">Leading</span>{' '}
              Print Publications
            </h1>
            
            <p className="font-manrope text-lg md:text-xl text-gray-400 mb-10 max-w-2xl">
              Establish credibility with coverage in India's most respected newspapers and magazines. Physical presence that commands attention and trust.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/marketplace?type=print"
                className="btn-shimmer inline-flex items-center justify-center gap-3 px-10 py-5 font-manrope text-sm uppercase tracking-widest"
              >
                Browse Publications
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="#media-types"
                className="inline-flex items-center justify-center gap-3 px-10 py-5 border-2 border-[#D4AF37]/50 text-white font-manrope text-sm uppercase tracking-widest hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all"
              >
                View Options
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#D4AF37] py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="font-mono text-3xl font-bold text-[#0d0d0d]">30+</div>
              <div className="font-manrope text-sm text-[#0d0d0d]/70">Publications</div>
            </div>
            <div>
              <div className="font-mono text-3xl font-bold text-[#0d0d0d]">50M+</div>
              <div className="font-manrope text-sm text-[#0d0d0d]/70">Daily Readers</div>
            </div>
            <div>
              <div className="font-mono text-3xl font-bold text-[#0d0d0d]">Pan India</div>
              <div className="font-manrope text-sm text-[#0d0d0d]/70">Coverage</div>
            </div>
            <div>
              <div className="font-mono text-3xl font-bold text-[#0d0d0d]">100%</div>
              <div className="font-manrope text-sm text-[#0d0d0d]/70">Publication Proof</div>
            </div>
          </div>
        </div>
      </section>

      {/* Media Types Section */}
      <section id="media-types" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#1a1a1a]">
              Print Media Options
            </h2>
            <p className="font-manrope text-[#737373] max-w-2xl mx-auto">
              Choose from newspapers, magazines, and regional publications
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mediaTypes.map((type, index) => {
              const Icon = type.icon;
              return (
                <div key={index} className="group bg-[#fafafa] border border-[#e5e5e5] p-8 hover:border-[#D4AF37] transition-all">
                  <div className="w-16 h-16 bg-[#0d0d0d] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#D4AF37] transition-colors">
                    <Icon className="w-8 h-8 text-[#D4AF37] group-hover:text-[#0d0d0d] transition-colors" />
                  </div>
                  <h3 className="font-playfair text-2xl font-bold mb-3 text-[#1a1a1a]">{type.name}</h3>
                  <p className="font-manrope text-sm text-[#737373] mb-4">{type.description}</p>
                  <div className="flex items-center gap-2 text-[#D4AF37] font-manrope text-sm font-semibold">
                    <TrendingUp className="w-4 h-4" />
                    {type.reach}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#f5f5f5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#1a1a1a]">
              Why Print Media Still Matters
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white border border-[#e5e5e5] p-6 hover:border-[#D4AF37] transition-colors">
                  <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <h3 className="font-playfair text-lg font-bold mb-2 text-[#1a1a1a]">{feature.title}</h3>
                  <p className="font-manrope text-sm text-[#737373]">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits & Sample Publications */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-6 text-[#1a1a1a]">
                Benefits of Print Coverage
              </h2>
              <p className="font-manrope text-[#737373] mb-8">
                Print media offers unique advantages that digital simply cannot replicate.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="font-manrope text-[#525252]">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <Link
                to="/marketplace?type=print"
                className="inline-flex items-center gap-2 mt-8 px-8 py-4 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-sm uppercase tracking-widest font-semibold hover:bg-[#B8960F] transition-colors"
              >
                Get Started
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="bg-[#0d0d0d] p-8 border border-[#D4AF37]/20">
              <h3 className="font-playfair text-xl font-bold text-white mb-6">Featured Publications</h3>
              {loading ? (
                <div className="text-gray-500">Loading...</div>
              ) : (
                <div className="space-y-4">
                  {printMedia.map((media) => (
                    <Link
                      key={media.id}
                      to={`/print-media/${media.id}`}
                      className="flex items-center justify-between p-4 bg-[#1a1a1a] border border-[#D4AF37]/10 hover:border-[#D4AF37]/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                          <Newspaper className="w-5 h-5 text-[#0d0d0d]" />
                        </div>
                        <div>
                          <div className="font-playfair font-bold text-white">{media.name}</div>
                          <div className="text-xs text-gray-500">{media.type} • {media.region || 'National'}</div>
                        </div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-500" />
                    </Link>
                  ))}
                </div>
              )}
              <Link
                to="/marketplace?type=print"
                className="block w-full mt-6 py-3 text-center text-[#D4AF37] font-manrope text-sm uppercase tracking-widest hover:text-white transition-colors"
              >
                View All Publications →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#f5f5f5]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-4 text-[#1a1a1a]">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-[#e5e5e5] bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-[#fafafa] transition-colors"
                >
                  <span className="font-manrope font-semibold text-[#1a1a1a] pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#737373] flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="font-manrope text-sm text-[#737373]">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#D4AF37]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold mb-6 text-[#0d0d0d]">
            Ready for Print Coverage?
          </h2>
          <p className="font-manrope text-[#0d0d0d]/80 mb-8 max-w-2xl mx-auto">
            Get featured in India's leading newspapers and magazines. Build credibility that lasts.
          </p>
          <Link
            to="/marketplace?type=print"
            className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-[#0d0d0d] text-white font-manrope text-sm uppercase tracking-widest hover:bg-black transition-all"
          >
            Browse Publications
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrintMedia;
